import React, { useState } from 'react';
import { ArrowLeft, Calculator, Info, AlertTriangle, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from 'react-i18next';
import YearSelector from '@/components/YearSelector';
import { useSEO } from '@/hooks/useSEO';

// Tax configuration for all countries and years
const TAX_CONFIG = {
  2025: {
    DE: {
      grundfreibetrag: 12096,
      zone1_end: 17443,
      zone2_end: 68480,
      zone3_end: 277825,
      soli_freigrenze: 19950,
      bbg_kv_pv: 66150,
      bbg_rv: 96600,
      gewst_freibetrag: 24500,
    },
    AT: {
      steuerfreigrenze: 13308,
      stufen: [
        { von: 0, bis: 13308, satz: 0 },
        { von: 13308, bis: 21617, satz: 20 },
        { von: 21617, bis: 35836, satz: 30 },
        { von: 35836, bis: 69166, satz: 40 },
        { von: 69166, bis: 103072, satz: 48 },
        { von: 103072, bis: 1000000, satz: 50 },
        { von: 1000000, bis: null, satz: 55 },
      ],
      svs_rate: 27.34,
      svs_mindestbeitragsgrundlage: 6613.56,
      svs_hoechstbeitragsgrundlage: 90300,
    },
    CH: {
      bundessteuer_freigrenze: 17800,
      ahv_rate: 10.0,
      ahv_min: 503,
    }
  },
  2026: {
    DE: {
      grundfreibetrag: 12348,
      zone1_end: 17820,
      zone2_end: 69970,
      zone3_end: 283515,
      soli_freigrenze: 20350,
      bbg_kv_pv: 69750,
      bbg_rv: 101400,
      gewst_freibetrag: 24500,
    },
    AT: {
      steuerfreigrenze: 13539,
      stufen: [
        { von: 0, bis: 13539, satz: 0 },
        { von: 13539, bis: 21992, satz: 20 },
        { von: 21992, bis: 36458, satz: 30 },
        { von: 36458, bis: 70365, satz: 40 },
        { von: 70365, bis: 104859, satz: 48 },
        { von: 104859, bis: 1000000, satz: 50 },
        { von: 1000000, bis: null, satz: 55 },
      ],
      svs_rate: 27.34,
      svs_mindestbeitragsgrundlage: 6728.08,
      svs_hoechstbeitragsgrundlage: 97020,
    },
    CH: {
      bundessteuer_freigrenze: 18100,
      ahv_rate: 10.0,
      ahv_min: 514,
    }
  }
};

// Helper function to format currency
const formatCurrency = (amount, currency = '€') => {
  return `${amount.toLocaleString('de-DE')} ${currency}`;
};

// Get tax config with fallback
const getTaxConfig = (year) => {
  if (TAX_CONFIG[year]) return TAX_CONFIG[year];
  const availableYears = Object.keys(TAX_CONFIG).map(y => parseInt(y)).sort((a, b) => b - a);
  return TAX_CONFIG[availableYears[0]];
};

export default function TaxDocumentation() {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // SEO configuration for Tax Documentation page
  useSEO({
    title: 'Steuerberechnung Dokumentation - Wie funktioniert die Steuerberechnung? | ivoliq',
    description: 'Detaillierte Erklärung der Steuerberechnung für Deutschland, Österreich und Schweiz. Einkommensteuer, Umsatzsteuer, Sozialversicherung - verständlich erklärt.',
    canonical: 'https://ivoliq.io/tax-documentation',
    robots: 'index,follow'
  });
  
  const config = getTaxConfig(selectedYear);
  const deConfig = config.DE;
  const atConfig = config.AT;
  const chConfig = config.CH;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/taxes" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('taxes.backToOverview', 'Back to Tax Overview')}
        </Link>

        {/* Header with Year Selector */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
            <h1 className="text-4xl font-bold text-gray-900 flex items-center">
              <Calculator className="h-10 w-10 mr-3 text-emerald-600" />
              {t('taxes.howItWorks', 'How Does Tax Calculation Work?')}
            </h1>
            <YearSelector 
              selectedYear={selectedYear}
              onYearChange={setSelectedYear}
              minYear={2025}
              maxYear={2026}
            />
          </div>
          <p className="text-lg text-gray-600">
            {t('taxes.explanation', 'Detailed explanation of our tax calculation methodology for Germany, Austria, and Switzerland')} – <strong>Steuerjahr {selectedYear}</strong>
          </p>
        </div>

        {/* Disclaimer */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start">
              <AlertTriangle className="h-6 w-6 text-amber-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Wichtiger Hinweis</h3>
                <p className="text-amber-800">
                  Diese Berechnungen sind <strong>vereinfacht</strong> und dienen nur zur <strong>groben Orientierung</strong>. 
                  Sie ersetzen <strong>KEINE professionelle Steuerberatung</strong>. Für verbindliche Steuererklärungen 
                  konsultieren Sie bitte einen Steuerberater oder das Finanzamt.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Germany Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              🇩🇪 Deutschland ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Einkommensteuer (§32a EStG)</h3>
              <p className="text-gray-700 mb-2">
                Die deutsche Einkommensteuer ist <strong>progressiv</strong> gestaltet. Der Steuersatz steigt mit dem Einkommen.
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Grundfreibetrag:</strong> {formatCurrency(deConfig.grundfreibetrag)} (steuerfrei)</li>
                <li><strong>Eingangssteuersatz:</strong> 14% ab {formatCurrency(deConfig.grundfreibetrag + 1)}</li>
                <li><strong>Progressionszone 1:</strong> 14-24% bis {formatCurrency(deConfig.zone1_end)}</li>
                <li><strong>Progressionszone 2:</strong> 24-42% bis {formatCurrency(deConfig.zone2_end)}</li>
                <li><strong>Spitzensteuersatz:</strong> 42% bis {formatCurrency(deConfig.zone3_end)}</li>
                <li><strong>Reichensteuer:</strong> 45% über {formatCurrency(deConfig.zone3_end)}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Solidaritätszuschlag</h3>
              <p className="text-gray-700 mb-2">
                5,5% der Einkommensteuer, aber erst ab einer Einkommensteuer von <strong>{formatCurrency(deConfig.soli_freigrenze)}</strong> (Freigrenze {selectedYear}).
                Ca. 90% der Steuerzahler zahlen keinen Soli mehr.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Gewerbesteuer (nur für Gewerbetreibende!)</h3>
              <p className="text-gray-700 mb-2">
                <strong>WICHTIG:</strong> Freiberufler zahlen <strong>KEINE</strong> Gewerbesteuer!
              </p>
              <p className="text-gray-700 mb-2">Berechnung für Gewerbetreibende:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Freibetrag:</strong> {formatCurrency(deConfig.gewst_freibetrag)} abziehen</li>
                <li><strong>Steuermesszahl:</strong> 3,5% auf den Rest</li>
                <li><strong>Hebesatz:</strong> × kommunaler Hebesatz (meist 350-450%)</li>
              </ol>
              <p className="text-gray-700 mt-2 text-sm italic">
                Beispiel: Bei 50.000 € Gewinn und 400% Hebesatz: (50.000 - {deConfig.gewst_freibetrag.toLocaleString('de-DE')}) × 3,5% × 4 = {((50000 - deConfig.gewst_freibetrag) * 0.035 * 4).toLocaleString('de-DE')} €
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">4. Beitragsbemessungsgrenzen ({selectedYear})</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Kranken-/Pflegeversicherung:</strong> {formatCurrency(deConfig.bbg_kv_pv)}</li>
                <li><strong>Rentenversicherung (West):</strong> {formatCurrency(deConfig.bbg_rv)}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">5. Umsatzsteuer (Mehrwertsteuer)</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Normalsatz:</strong> 19%</li>
                <li><strong>Ermäßigter Satz:</strong> 7% (Lebensmittel, Bücher, etc.)</li>
                <li><strong>Steuerfrei:</strong> 0% (bestimmte Dienstleistungen)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Austria Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              🇦🇹 Österreich ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Einkommensteuer</h3>
              <p className="text-gray-700 mb-2">
                Progressive Steuersätze in 7 Stufen:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Grundfreibetrag:</strong> {formatCurrency(atConfig.steuerfreigrenze)} (steuerfrei)</li>
                {atConfig.stufen.slice(1).map((stufe, idx) => (
                  <li key={idx}>
                    <strong>{stufe.satz}%:</strong> {formatCurrency(stufe.von)} - {stufe.bis ? formatCurrency(stufe.bis) : 'unbegrenzt'}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Sozialversicherung (SVS) für Selbstständige</h3>
              <p className="text-gray-700 mb-2">
                Pflichtversicherung ab {formatCurrency(atConfig.svs_mindestbeitragsgrundlage)} Jahreseinkommen:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Pensionsversicherung:</strong> 20,15%</li>
                <li><strong>Krankenversicherung:</strong> 6,80%</li>
                <li><strong>Unfallversicherung:</strong> 0,39%</li>
                <li><strong>Gesamt:</strong> ca. {atConfig.svs_rate}%</li>
              </ul>
              <p className="text-gray-700 mt-2 text-sm">
                <strong>Höchstbeitragsgrundlage ({selectedYear}):</strong> {formatCurrency(atConfig.svs_hoechstbeitragsgrundlage)}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Umsatzsteuer</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Normalsatz:</strong> 20%</li>
                <li><strong>Ermäßigter Satz:</strong> 13% (Lebensmittel, etc.)</li>
                <li><strong>Ermäßigter Satz:</strong> 10% (Bücher, Kultur, etc.)</li>
                <li><strong>Steuerfrei:</strong> 0%</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Switzerland Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              🇨🇭 Schweiz ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Bundessteuer (Direkte Bundessteuer)</h3>
              <p className="text-gray-700 mb-2">
                Progressive Bundessteuer mit Freibetrag von <strong>{formatCurrency(chConfig.bundessteuer_freigrenze, 'CHF')}</strong>. Steuersätze von 0% bis 11%.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Kantonale & Gemeindesteuern</h3>
              <p className="text-gray-700 mb-2">
                <strong>WICHTIG:</strong> Steuersätze variieren <strong>stark</strong> zwischen Kantonen!
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Niedrigste:</strong> Zug (ca. 3,5-7%)</li>
                <li><strong>Mittel:</strong> Zürich (ca. 8-13%)</li>
                <li><strong>Höchste:</strong> Genf (ca. 12-18%)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Sozialversicherung (AHV/IV/EO)</h3>
              <p className="text-gray-700 mb-2">
                Für Selbstständige: <strong>{chConfig.ahv_rate}%</strong> des Einkommens
              </p>
              <p className="text-gray-700 text-sm">
                Minimalbeitrag ({selectedYear}): {formatCurrency(chConfig.ahv_min, 'CHF')} pro Jahr
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">4. Mehrwertsteuer (MWST)</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Normalsatz:</strong> 8,1%</li>
                <li><strong>Sondersatz Beherbergung:</strong> 3,8%</li>
                <li><strong>Reduzierter Satz:</strong> 2,6%</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Calculation Methodology */}
        <Card className="mb-6 border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl text-emerald-700">
              <Info className="h-6 w-6 mr-2" />
              Berechnungsmethodik
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Vereinfachungen</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Berechnung für <strong>Alleinstehende</strong> (Steuerklasse I in DE)</li>
                <li>Keine Berücksichtigung von Kinderfreibeträgen</li>
                <li>Keine außergewöhnlichen Belastungen</li>
                <li>Keine Sonderausgaben über Pauschbeträge hinaus</li>
                <li>Keine Kirchensteuer (ca. 8-9% der ESt zusätzlich)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">Automatischer Jahreswechsel</h3>
              <p className="text-gray-700">
                Alle Steuerwerte werden automatisch zum Jahreswechsel aktualisiert. 
                Die Berechnung verwendet immer die für das gewählte Jahr gültigen Werte.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to Taxes */}
        <div className="text-center pb-8">
          <Link 
            to="/taxes" 
            className="inline-flex items-center bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Calculator className="h-5 w-5 mr-2" />
            Zur Steuerberechnung
          </Link>
        </div>
      </div>
    </div>
  );
}
